import { useState } from "react";

import { useApiCommand } from "../../../api/hooks";
import { refusalText } from "../../../api/errors";
import type { Id, LockView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { useLock } from "../../../live";
import { Banner, Button, ConfirmDialog } from "../../../ui";
import { useRefresh } from "../hooks";
import { whenWords } from "../lib/text";

interface LockBannerProps {
  elementId: Id;
  /** The lock the server reported with the element, if any. */
  lock?: LockView | null;
  /** Called when the person took the lock over, so the screen can read the element again. */
  onTakenOver?: () => void;
}

/**
 * Says who is editing an element and lets an engineer ask them to let go, or take over. A lock stops others from
 * changing the element, never from reading it (12.3).
 */
export function LockBanner({ elementId, lock, onTakenOver }: LockBannerProps) {
  const me = useCurrentMe();
  const live = useLock(elementId);
  const refresh = useRefresh();
  const ask = useApiCommand("ask_lock");
  const takeOver = useApiCommand("take_over_lock");
  const [confirming, setConfirming] = useState(false);

  const holder = lock?.holder ?? live?.holder ?? null;
  if (!holder) return null;
  const mine = !!holder.membership_id && holder.membership_id === me.active?.membership_id;
  const takenAt = lock?.taken_at ? ` since ${whenWords(lock.taken_at)}` : "";
  const asked = lock?.release_asked_by ?? live?.release_asked_by ?? null;
  const isEngineer = me.active?.roles.includes("engineer") ?? false;

  if (mine) {
    return <Banner>You are editing this. Others can read it but cannot change it until you save or cancel.</Banner>;
  }

  const error = ask.error ?? takeOver.error;
  return (
    <Banner tone="note">
      <p className="lock-banner__text">
        Being edited by {holder.display}
        {takenAt}. You can read it, but you cannot change it until they are done.
        {asked ? ` ${asked.display} has asked them to let go.` : ""}
      </p>
      {isEngineer && (
        <div className="row">
          <Button
            variant="secondary"
            busy={ask.isPending}
            disabled={ask.isSuccess}
            onClick={() =>
              ask.mutate(
                { id: elementId },
                {
                  onSuccess: () => refresh("element"),
                },
              )
            }
          >
            {ask.isSuccess ? "Asked" : `Ask ${holder.display} to let go`}
          </Button>
          <Button variant="quiet" onClick={() => setConfirming(true)}>
            Take over
          </Button>
        </div>
      )}
      {error && <p className="form-error">{refusalText(error)}</p>}
      <ConfirmDialog
        open={confirming}
        title="Take over the edit?"
        message={`${holder.display} will be told that you took over. Anything they have not saved is lost.`}
        confirmLabel="Take over"
        busy={takeOver.isPending}
        error={takeOver.error ? refusalText(takeOver.error) : null}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          takeOver.mutate(
            { id: elementId },
            {
              onSuccess: () => {
                setConfirming(false);
                refresh("element", "session_cards", "item_snapshot");
                onTakenOver?.();
              },
            },
          )
        }
      />
    </Banner>
  );
}
